// © Microsoft Corporation. All rights reserved.

import { CustomDeferralEventTypes, DeferralEventType, ExperienceDeferralEventTypes, ExperienceDeferralInfo, ExperienceDeferralMetadata, ExperienceLoadingState, ExperienceMilestoneEventTypes } from "./data-model";
import { IActionPayload, PageBase } from "@msnews/experiences-redux";
import { MilestoneDataActions, UpdateExperienceCurrentStateArgs, UpdateExperienceDeferralArgs, UpdateTimingArgs } from "./MilestoneData.actions";
import { defaultInstanceKeyName, defaultTimeValue } from "./MilestoneData.constants";

import { ConnectorNamespaces } from "@msnews/experiences-constants";
import { ExperienceInstanceMilestoneModel } from "./data-model/ExperienceInstanceMilestoneModel";
import { MilestoneDataConnector } from "./MilestoneData.connector";
import { MilestonePluginEvents } from "./data-model/MilestonePluginEvents";
import { headData } from "@msnews/core";
import { isExperienceDependencyResolved } from "./data-management/ExperienceDeferralDependencyTrackerState";
import { pluginUtils } from "./utils/MilestonePluginUtils";

/**
 * Helper method to get the milestone data connector instance.
 * @returns Milestone data connector promise.
 */
export function getMilestoneDataConnector(): MilestoneDataConnector {
    return PageBase.getInstance().rootReducer.connector(
        ConnectorNamespaces.MilestoneData
    ) as MilestoneDataConnector;
}

/**
 * Helper method to get default instance of an experience.
 * @returns Experience Milestone instance data.
 */
export function getDefaultExperienceInstance(
    experienceType: string
): ExperienceInstanceMilestoneModel {
    const connector = getMilestoneDataConnector();

    if (!connector) {
        return;
    }

    const milestones = connector.getCurrentState().perExperienceMilestones;
    const experience = milestones && milestones.get(experienceType);
    let instance: ExperienceInstanceMilestoneModel;

    if (experience) {
        instance = experience.perInstanceMilestones.get(defaultInstanceKeyName);
    }

    return instance;
}

/**
 * returns promise based experience load delegate, which resolves when experience dependent event is fired.
 * @returns A callback promise which resolves to boolean when associated experience is ready to be loaded
 * If null is returned experience is not deferred and should be loaded/rendered right away.
 */
export function getExperienceLoadDelegate(
    experienceType: string
): ExperienceDeferralMetadata {

    const instance: ExperienceInstanceMilestoneModel = getDefaultExperienceInstance(experienceType);

    // return null if dependent deferral event has already been fired
    if (!instance || getDeferralLoadEventReadyState(instance)) {
        return null;
    }

    if (instance.shouldLoad && instance.loadAfter) {

        const deferralEventType = getDeferralEventMetadata(instance.loadAfter);
        return { deferralType: deferralEventType, shouldResolve: instance.shouldLoad };
    }

    return null;
}

/**
 * returns a promise that is resolved when
 * Milestone data connector was not initialized
 * Milestone data connector has already fired the above the fold visual ready action
 * Milestone data connector hasn't fired above the fold visual ready action so an observer is
 * set on the action to resolve the promise when that callback is invoked
 */
export function waitForAboveTheFoldVisuallyReadyAsync(): Promise<void> {
    const milestoneDataConnector = getMilestoneDataConnector();

    if (!milestoneDataConnector) {
        return Promise.resolve();
    }

    if (isAboveTheFoldVisuallyReady()) {
        return Promise.resolve();
    }

    return new Promise(resolve => {
        MilestoneDataActions.updateAboveTheFoldVisuallyReady.registerObserver(((actionPayload: IActionPayload) => {
            resolve();
        }));
    });
}

/**
 * returns a promise that is resolved when
 * Milestone data connector was not initialized
 * Milestone data connector has already fired the page reveal started action
 * Milestone data connector hasn't fired page reveal started action so an observer is
 * set on the action to resolve the promise when that callback is invoked
 */
export function requestPageRevealStartedAsync(): Promise<void> {
    const milestoneDataConnector = getMilestoneDataConnector();

    if (!headData.IsPrerender || !milestoneDataConnector) {
        return Promise.resolve();
    }

    if (isPageRevealStarted()) {
        return Promise.resolve();
    }

    return new Promise(resolve => {
        MilestoneDataActions.updatePageRevealStart.registerObserver(((actionPayload: IActionPayload) => {
            resolve();
        }));
    });
}

/**
 * returns a promise that is resolved when
 * Milestone data connector was not initialized
 * Milestone data connector has already fired the page reveal completed action
 * Milestone data connector hasn't fired page reveal completed action so an observer is
 * set on the action to resolve the promise when that callback is invoked
 */
export function requestPageRevealCompletedAsync(): Promise<void> {
    const milestoneDataConnector = getMilestoneDataConnector();

    if (!headData.IsPrerender || !milestoneDataConnector) {
        return Promise.resolve();
    }

    if (isPageRevealCompleted()) {
        return Promise.resolve();
    }

    return new Promise(resolve => {
        MilestoneDataActions.updatePageRevealComplete.registerObserver(((actionPayload: IActionPayload) => {
            resolve();
        }));
    });
}

/**
 * returns a promise that is resolved when
 * Milestone data connector was not initialized
 * Milestone data connector hasn't fired page reveal started action so an observer is
 * set on the action to resolve the promise when that callback is invoked
 */
export function pageRevealStartedObserver(): Promise<void> {
    const milestoneDataConnector = getMilestoneDataConnector();

    if (!milestoneDataConnector) {
        return Promise.resolve();
    }

    return new Promise(resolve => {
        MilestoneDataActions.updatePageRevealStart.registerObserver(((actionPayload: IActionPayload) => {
            resolve();
        }));
    });
}

/**
 * returns promise based experience render delegate, which resolves when experience dependent event is fired.
 * @returns A callback promise which resolves to boolean when associated experience is ready to be rendered.
 * If null is returned experience is not deferred and should be loaded/rendered right away.
 */
export function getExperienceRenderDelegate(
    experienceType: string
): ExperienceDeferralMetadata {

    const instance: ExperienceInstanceMilestoneModel = getDefaultExperienceInstance(experienceType);

    // return null if dependent deferral event has already been fired
    if (!instance || getDeferralRenderEventReadyState(instance)) {
        return null;
    }

    if (instance.shouldRender && instance.renderAfter) {

        const deferralEventType = getDeferralEventMetadata(instance.renderAfter);
        return { deferralType: deferralEventType, shouldResolve: instance.shouldRender };
    }

    return null;
}

/**
 * Execute milestone event hooks
 */
export async function executeMilestoneEventPlugins(eventType: MilestonePluginEvents): Promise<void> {

    const connector = getMilestoneDataConnector();

    if (!connector) {
        return;
    }

    const plugins = connector.getCurrentState().plugins;

    if (!plugins || plugins.length === 0) {
        return;
    }

    const filteredPlugins = pluginUtils.filter(plugins, eventType);

    for (const plugin of filteredPlugins) {
        if (plugin[eventType] !== undefined) {
            await plugin[eventType].call(plugin);
        }
    }
}

/**
 * Return dependent deferral event ready state
 * @param instance - Experience instance
 */
function getDeferralLoadEventReadyState(instance: ExperienceInstanceMilestoneModel): boolean {

    let deferralEventReady: boolean;

    switch (instance.loadAfter.eventType) {
        case ExperienceDeferralEventTypes.aboveTheFoldVisuallyReady:
            deferralEventReady = isAboveTheFoldVisuallyReady();
            break;

        case ExperienceDeferralEventTypes.belowTheFoldTransitionStart:
            deferralEventReady = isBelowTheFoldTransitionStarted();
            break;

        case ExperienceDeferralEventTypes.hydrationComplete:
            deferralEventReady = isHydrationComplete();
            break;

        default:
            deferralEventReady = isExperienceDependencyResolved(instance.loadAfter.experienceType, instance.loadAfter.eventType);
    }

    return deferralEventReady;
}

/**
 * Return dependent deferral event ready state
 * @param instance - Experience instance
 */
function getDeferralRenderEventReadyState(instance: ExperienceInstanceMilestoneModel): boolean {

    let deferralEventReady: boolean;

    switch (instance.renderAfter.eventType) {
        case ExperienceDeferralEventTypes.aboveTheFoldVisuallyReady:
            deferralEventReady = isAboveTheFoldVisuallyReady();
            break;

        case ExperienceDeferralEventTypes.belowTheFoldTransitionStart:
            deferralEventReady = isBelowTheFoldTransitionStarted();
            break;

        case ExperienceDeferralEventTypes.hydrationComplete:
            deferralEventReady = isHydrationComplete();
            break;

        default:
            deferralEventReady = isExperienceDependencyResolved(instance.loadAfter.experienceType, instance.renderAfter.eventType);
    }

    return deferralEventReady;
}

/**
 * Returns true if above the fold is ready
 */
function isAboveTheFoldVisuallyReady(): boolean {
    const connector = getMilestoneDataConnector();

    if (!connector) {
        return false;
    }

    const { aboveTheFoldVisuallyReady } = connector.getCurrentState();
    return Boolean(
        aboveTheFoldVisuallyReady
        && aboveTheFoldVisuallyReady.endTime !== defaultTimeValue
    );
}

/**
 * Returns true if page reveal has started
 */
function isPageRevealStarted(): boolean {
    const connector = getMilestoneDataConnector();

    if (!connector) {
        return false;
    }

    const { pageReveal } = connector.getCurrentState();
    return Boolean(
        pageReveal
        && pageReveal.startTime!== defaultTimeValue
    );
}

/**
 * Returns true if page reveal has completed
 */
function isPageRevealCompleted(): boolean {
    const connector = getMilestoneDataConnector();

    if (!connector) {
        return false;
    }

    const { pageReveal } = connector.getCurrentState();
    return Boolean(
        pageReveal
        && pageReveal.endTime!== defaultTimeValue
    );
}

/**
 * Returns true if below the fold transition has started
 */
function isBelowTheFoldTransitionStarted(): boolean {
    const connector = getMilestoneDataConnector();

    if (!connector) {
        return false;
    }

    return connector.getCurrentState().belowTheFoldTransitionStart;
}

/**
 * Update deferral event metadata
 * @param deferralInfo - Tracks deferral information used by an experience to determine when it should load/render
 * @returns - DeferralEventType, this can be either standard or custom depending upon what type of deferral has been used for experience deferral
 */
function getDeferralEventMetadata(deferralInfo: ExperienceDeferralInfo): DeferralEventType {
    return deferralInfo.eventType in CustomDeferralEventTypes
        ? DeferralEventType.CustomEventBasedDeferral
        : DeferralEventType.StandardEventBasedDeferral;
}

/**
 * Return trues if hydration of SSR response is complete
 */
function isHydrationComplete(): boolean {
    const connector = getMilestoneDataConnector();

    if (!connector) {
        return false;
    }

    return connector.getCurrentState().hydrationComplete;
}

/**
 * Helper to create deferral info
 * @param deferralInfo - Tracks deferral information used by an experience to determine when it should load/render
 * @param experienceType - Experience Type - Defines an experience
 * @param experienceGroup - Associated Experience Group
 * @param experienceInstance - Defines an instance of the Experience
 */
export function createDeferralInfo(
    deferralInfo: ExperienceDeferralInfo,
    experienceType: string,
    experienceGroup?: string,
    experienceInstance?: string
): UpdateExperienceDeferralArgs {

    return {
        deferralInfo,
        experienceType,
        experienceGroup,
        experienceInstance
    };
}

/**
 * Helper to create experience state update arguments.
 * @param experienceType - Experience Type - Defines an experience
 * @param currentState - Current Milestone of the experience e.g initialized, loaded, deferred etc
 * @param experienceGroup - Associated Experience Group
 * @param experienceInstance - Defines an instance of the Experience
 */
export function createExperienceStateUpdate(
    experienceType: string,
    currentState: ExperienceLoadingState,
    experienceGroup?: string,
    experienceInstance?: string
): UpdateExperienceCurrentStateArgs {

    return {
        experienceType,
        currentState,
        experienceGroup,
        experienceInstance
    };
}

/**
 * Creates a timing update arguments
 * @param experienceType - Experience Type - Defines an experience
 * @param experienceGroup - Associated Experience Group
 * @param experienceInstance - Defines an instance of the Experience
 * @param startTime - start time fo the milestone e.g. startTime of loaded milestone
 * @param endTime - end time fo the milestone e.g. endTime of loaded milestone
 */
export function createTimingUpdate(
    experienceType: string,
    experienceGroup?: string,
    experienceInstance?: string,
    startTime?: number,
    endTime?: number
): UpdateTimingArgs {

    return {
        experienceType,
        experienceGroup,
        experienceInstance,
        startTime,
        endTime
    };
}